import { CSSObject } from "@emotion/serialize";
import { merge } from "lodash";
import { padding } from "polished";

import type { BreakPointVals } from "@brexhq/shared/styles/breakpoints";
import { defaultTheme } from "@brexhq/shared/styles/theming";
import {
  desktop,
  tablet,
  mobile,
  type SpacingValues,
} from "@brexhq/shared/types/SpacingScale";

import { ArrayTypes, SpacingProp } from "./types";

export const isNotSpacerObject = (value: any): value is ArrayTypes => {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    Array.isArray(value)
  );
};

/**
 * Utility function for adding spacer padding to styled component
 * @param values object with LRG, MED and SML padding arrays/value
 * @returns CSSObject with padding defined at SML and MED breakpoints, LRG is default
 *
 */
export const getResponsiveSpacer = (values: SpacingProp): CSSObject => {
  const { breakpoints } = defaultTheme;
  return {
    ...padding(...convertSpacersToNumericArray(values, "LRG")),
    [breakpoints.MED]: {
      ...padding(...convertSpacersToNumericArray(values, "MED")),
    },
    [breakpoints.SML]: {
      ...padding(...convertSpacersToNumericArray(values, "SML")),
    },
  };
};

const evalBreakpoint = (
  str: TemplateStringsArray,
  breakpoint: string[],
  value: Array<string | number>,
) => {
  return value.length !== 0
    ? str[0] + breakpoint + str[1] + evalProp` ${value}` + str[2]
    : "";
};
const evalProp = (
  attribute: TemplateStringsArray,
  value: Array<string | number>,
) => {
  return value.length !== 0 ? attribute[0] + value.join("px ") + "px;" : "";
};

/**
 * Utility function for adding spacer padding to styled component
 * @param values object with LRG, MED and SML padding arrays/value
 * @returns string with padding defined at SML and MED breakpoints, LRG is default
 *
 */
export const getResponsiveSpacerString = (values: SpacingProp): string => {
  const { breakpoints } = defaultTheme;

  let paddingString = "";

  paddingString += evalProp`padding: ${convertSpacersToNumericArray(
    values,
    "LRG",
  )}`;

  paddingString += evalBreakpoint`
  ${[breakpoints.MED]} {
    padding: ${convertSpacersToNumericArray(values, "MED")}};
  `;

  paddingString += evalBreakpoint`
  ${[breakpoints.SML]}{
    padding: ${convertSpacersToNumericArray(values, "SML")} };
  `;

  return paddingString;
};

export const convertSpacersToNumericArray = (
  values: SpacingProp | undefined,
  breakpoint: BreakPointVals,
): Array<string | number> => {
  const isArray = Array.isArray(values);

  if (values === undefined) return [];
  if (isNotSpacerObject(values)) {
    return isArray
      ? values.map((val) => convertSpacerValueToNumeric(val, breakpoint))
      : [convertSpacerValueToNumeric(values, breakpoint)];
  }
  return convertSpacersToNumericArray(values[breakpoint], breakpoint);
};

/**
 * Utility function for adding spacer padding to styled component
 * @param value one of the spacing tokens or 0
 * @param breakpoint the breakpoint that you want to convert in
 * @returns number that is converted from the spacing token
 */
export const convertSpacerValueToNumeric = (
  value: SpacingValues | 0,
  breakpoint: BreakPointVals,
): number => {
  if (value === 0) {
    return 0;
  }

  if (breakpoint === "SML") {
    return mobile[value];
  }

  if (breakpoint === "MED") {
    return tablet[value];
  }

  return desktop[value];
};

/**
 * Utility function for adding spacer padding to styled component
 * @param values object with LRG, MED and SML set to array with max length of 4 or a value  ie ["xl", "xl", "lrg", "sml"] or "sml"
 * @param original CSSObject or css string that you want to merge padding into
 * @returns merged CSSObject or css string adding padding to each breakpoint
 *
 */
export const withPadding = (
  values: SpacingProp,
  original: CSSObject | string,
): CSSObject | string => {
  if (typeof original === "string") {
    return original + getResponsiveSpacerString(values);
  }
  return merge(
    getBreakpointOrder(original),
    original,
    getResponsiveSpacer(values),
  );
};

/**
 * Helper function that makes sure the order of breakpoints is accurate for merging padding into CSSObject
 * @param original CSSObject that you want to merge padding into
 * @returns CSSObject that has breakpoints set in order they are needed
 *
 */
const getBreakpointOrder = (original: CSSObject): CSSObject => {
  const { breakpoints } = defaultTheme;

  const breakPointOrder = {};

  for (const breakpoint in breakpoints) {
    if (
      original[breakpoint] ||
      breakpoint == breakpoints.MED ||
      breakpoint == breakpoints.SML
    ) {
      breakPointOrder[breakpoint] = {};
    }
  }
  return breakPointOrder;
};
